import React from 'react';
import classNames from 'classnames';
import Icon, { IconName } from 'app/common/components/controls/icon';
import styles from './UpgradePromptFull.less';
import { ComponentWrapper } from 'app/src/components/ComponentWrapper';
import RouterLink from 'app/common/components/singlespa/router-link';

/**
 * UpgradePromptFull props
 *
 * content: prompt copy, optionally rendered
 * cta: call to action of prompt, navigates user within the page
 * ctaLink: link for call to action
 * ctaTrackEvent: function expected to fire a trackUe event, fired on click of cta
 * headline: prompt header
 * iconName: name of icon
 * isDarkTheme: controls dark theme
 * isDismissable: optonially renders close button
 * onDismiss: function expected to unmount prompt and make dismissal persistent for the user
 */

interface UpgradePromptFullProps {
  className?: string;
  content?: string;
  cta: string;
  ctaLink: string;
  ctaTrackEvent?: () => void;
  headline: string;
  iconName: IconName;
  isDarkTheme?: boolean;
  isDismissable?: boolean;
  onDismiss?: () => void;
}

export const UpgradePromptFull: React.FunctionComponent<
  UpgradePromptFullProps
> = ({
  className,
  content,
  cta,
  ctaLink,
  ctaTrackEvent,
  headline,
  iconName,
  isDarkTheme,
  isDismissable,
  onDismiss,
}) => {
  const onClickCTA = () => {
    if (ctaTrackEvent) {
      ctaTrackEvent();
    }
  };

  const promptClasses = classNames(
    styles.upgradePromptFull,
    isDarkTheme
      ? styles.upgradePromptFullDarkTheme
      : styles.upgradePromptFullLightTheme,
    isDarkTheme && 'upgrade-prompt-full-dark',
    className,
  );

  const contentBlock = content && (
    <div
      className={classNames(
        styles.upgradePromptFullContent,
        'upgrade-prompt-full-content',
      )}
    >
      {content}
    </div>
  );

  const closeButton = isDismissable && (
    <button
      className={classNames(
        styles.upgradePromptFullCloseButtonWrapper,
        'upgrade-prompt-close-button',
      )}
      onClick={onDismiss}
    >
      <Icon
        className={styles.upgradePromptFullCloseButton}
        name={IconName.Close}
        small={true}
      />
    </button>
  );

  return (
    <div className={promptClasses}>
      <div className={styles.upgradePromptFullHeadline}>{headline}</div>
      {contentBlock}
      <RouterLink
        href={ctaLink}
        className={classNames(
          styles.upgradePromptFullCTA,
          'upgrade-prompt-full-cta',
        )}
        onClick={onClickCTA}
      >
        {cta}
      </RouterLink>
      <div className={styles.upgradePromptFullIconBackground}>
        <Icon
          className={styles.upgradePromptFullIcon}
          name={iconName}
          small={true}
        />
      </div>
      <div className={styles.upgradePromptFullIconClippedBackground} />
      {closeButton}
    </div>
  );
};

export const UpgradePromptFullConnected: React.FunctionComponent<
  UpgradePromptFullProps
> = (props) => (
  <ComponentWrapper>
    <UpgradePromptFull {...props} />
  </ComponentWrapper>
);
